<template>
  <div
    class="node-details-wrap"
    v-loading="loading"
    v-cloak
    element-loading-text="加载中..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.8)"
  >
    <el-tabs @tab-click="loading1s" type="border-card">
      <el-tab-pane label="统计数据">
        <div class="left-chart">
          <v-chart v-if="hasStatPie" :options="pathogenStatByDayPie.options" :autoresize="true" />
          <span v-else>暂无阳性数据</span>
        </div>
        <div class="stat-table">
          <MTable v-bind="statTableConfig" @search="initDetectList" />
        </div>
      </el-tab-pane>
      <el-tab-pane label="检测信息">
        <MTable v-bind="tableConfig1"></MTable>
      </el-tab-pane>
      <el-tab-pane label="设备信息">
        <MTable v-bind="tableConfig2">
          <template #img="{ value }">
            <span v-if="!value">--</span>
            <el-image
              v-else
              :src="value"
              :appendToBody="false"
              :preview-src-list="[value]"
              :previewWrapAppendBody="false"
              previewContainer=".rteao-monitor"
              style="width: 100px; height: 100px"
            ></el-image>
          </template>
        </MTable>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script>
import MTable from '@/components/mTable/m-table.vue';
import { makeLineOptions, makePieOptions } from '../situation/utils';
import {
  getDeviceTestCountByOfficeId,
  getPathogenCountByOffice,
  getNodeDetectList
} from '@/api/RTEAO';
import { formatTime } from '@/utils/date';
import { sleep } from '@/utils/utils';

export default {
  components: {
    MTable
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    params: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: true,
      detectCount: 0,
      positiveCount: 0,
      statTableConfig: {
        key: 'table1',
        height: '100%',
        pagiOption: {
          offset: 1,
          limit: 10,
          total: 0,
          pagiSize: [10, 15, 20, 30],
          layout: 'total, sizes, prev, pager, next, jumper'
        },
        tableData: [],
        tableColumn: [
          {
            label: '采样开始时间',
            prop: 'sampleStartTime',
            width: '240px',
            temp: ({ row }) => {
              return formatTime(row.sampleStartTime, 'yyyy-MM-dd hh:mm');
            }
          },
          {
            label: '采样结束时间',
            prop: 'sampleEndTime',
            width: '240px',
            temp: ({ row }) => {
              return formatTime(row.sampleEndTime, 'yyyy-MM-dd hh:mm');
            }
          },
          {
            label: '哨点',
            prop: 'name'
          },
          {
            label: '病毒名称',
            prop: 'pathogenName'
          },
          {
            label: 'CT值',
            prop: 'pathogenValue',
            width: '120px',
            temp: ({ row }) => {
              return row.pathogenValue >= 40 ? 'UD' : row.pathogenValue;
            }
          },
          {
            label: '检测结果',
            prop: 'pathogenState',
            width: '140px',
            temp: ({ row }) => {
              return row.pathogenState == '1' ? '阳性' : '阴性';
            }
          }
        ]
      },
      tableConfig1: {
        key: 'table2',
        height: '100%',
        hasPagination: false,
        tableData: [],
        tableColumn: [
          {
            label: '设备名称',
            prop: 'name'
          },
          {
            label: '设备编号',
            prop: 'sn'
          },
          {
            label: '检测结果数量',
            prop: 'total'
          },
          {
            label: '阳性检出次数',
            prop: 'bad'
          }
        ]
      },
      tableConfig2: {
        key: 'table3',
        height: '100%',
        hasPagination: false,
        tableData: [],
        tableColumn: [
          {
            label: '设备位置',
            prop: 'name'
          },
          {
            label: '设备编号',
            prop: 'sn'
          },
          {
            label: '布置时间点',
            prop: 'timePoints',
            temp: ({ row }) => {
              return formatTime(row.timePoints, 'yyyy-MM-dd hh:mm');
            }
          },
          {
            label: '采样周期',
            prop: 'samplePeriod'
          },
          {
            label: '设备位置照片',
            prop: 'deviceLocationPhoto',
            slot: 'img'
          },
          {
            label: '负责人',
            prop: 'responsiblePersonality'
          },
          {
            label: '最近检测时间',
            prop: 'sampleSubmissionTime',
            temp: ({ row }) => {
              return formatTime(row.sampleSubmissionTime, 'yyyy-MM-dd hh:mm');
            }
          }
        ]
      },
      pathogenStatByDayData: [],
      pathogenStatByHalfYearData: [],
      pathogenStatByDayPie: makePieOptions(),
      pathogenStatByHalfYearLine: makeLineOptions()
    };
  },
  computed: {
    hasStatPie() {
      return this.pathogenStatByDayData.length > 0;
    }
  },
  methods: {
    async loading1s() {
      this.loading = true;
      await sleep(500);
      this.loading = false;
    },
    initLeftCharts() {
      this.pathogenStatByDayPie.setData(
        this.pathogenStatByDayData.map(e => ({
          name: `${e.pathogenName}(${e.value})`,
          value: e.value
        })),
        {
          series: [
            {
              radius: '35%'
            }
          ]
        }
      );

      const dimensions = new Set();
      dimensions.add('date');
    },
    async initStatData() {
      try {
        const { timeRange, pathogenList } = this.params;

        const [pieRes] = await Promise.all([
          getPathogenCountByOffice({
            officeIds: [this.id],
            beginTimestamp: timeRange[0],
            endTimestamp: timeRange[1],
            pathogenTypes: pathogenList
          })
        ]);

        this.pathogenStatByDayData.splice(0, Infinity, ...pieRes.data);
      } catch (error) {
        console.log(error);
      }
    },
    async initDetectInfo() {
      try {
        const { timeRange, pathogenList } = this.params;

        const res = await getDeviceTestCountByOfficeId({
          officeIds: [this.id],
          beginTimestamp: timeRange[0],
          endTimestamp: timeRange[1],
          pathogenTypes: pathogenList
        });
        if (res.code !== 200) return;

        this.tableConfig1.tableData.splice(0, Infinity, ...res.data);
        this.tableConfig2.tableData.splice(0, Infinity, ...res.data);
      } catch (error) {
        console.log(error);
      }
    },
    async initDetectList() {
      try {
        this.loading = true;

        await sleep(500);

        const { timeRange, pathogenList } = this.params;

        const { offset, limit } = this.statTableConfig.pagiOption;

        const res = await getNodeDetectList({
          officeIds: [this.id],
          beginTimestamp: timeRange[0],
          endTimestamp: timeRange[1],
          pathogenTypes: pathogenList,
          offset: offset,
          limit: limit
        });
        if (res.code !== 200) return;

        this.statTableConfig.pagiOption.total = res.data.total;
        this.statTableConfig.tableData.splice(0, Infinity, ...res.data.list);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    }
  },
  async mounted() {
    this.initDetectInfo();
    this.initDetectList();
    await this.initStatData();
    this.initLeftCharts();
  }
};
</script>

<style lang="scss">
.node-details-wrap {
  height: 65vh;

  .m-table {
    width: 100%;
  }

  .left-chart {
    width: 30%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }

  .stat-table {
    width: 70%;
    height: 100%;

    .m-table {
      height: calc(100% - 3.7rem);
    }
  }

  .m-table {
    height: 100%;
    .el-table {
      border-bottom: 0;
    }
  }

  .el-tabs {
    height: 100%;

    &__item {
      font-size: 1rem;
      height: 2rem;
      line-height: 2rem;
    }

    &__content {
      height: calc(100% - 40px);
      padding: 0 !important;

      .el-tab-pane {
        display: flex;
        height: 100%;
      }
    }
  }
}
</style>
